'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Suspense, useRef } from 'react';

import { DestinationsNavDropdown } from '@/components/header/DestinationsNavDropdown';
import { useHeaderMenuContent } from '@/lib/context/HeaderMenuContentContext';
import { useHeaderDropdownPosition } from '@/components/header/useHeaderDropdownPosition';
import { useHoverMenuOpen } from '@/components/header/useHoverMenuOpen';
import { getVisibleExperiences, toursLinkClass, triggerClass } from '@/components/header/navShared';
import paths from '@/paths';

const menuLinkClass =
  'block px-4 py-2 text-sm font-inter font-light text-gray-200 transition-colors hover:bg-gray-800/80 hover:text-primary-400';

function NavHoverDropdown({
  label,
  active,
  children,
  menuId,
  narrow = false,
}: {
  label: string;
  active: boolean;
  children: React.ReactNode;
  menuId: string;
  /** Narrow panel: right edge aligned with trigger’s right edge (end of label, e.g. “Experiences”) */
  narrow?: boolean;
}) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const { topPx, rightPx, anchorRightPx, measureSync } = useHeaderDropdownPosition(triggerRef, {
    alignMenuRightEdgeToTriggerRight: narrow,
  });
  const { open, openMenu, scheduleClose } = useHoverMenuOpen();

  const handleWrapperEnter = () => {
    openMenu();
    measureSync();
  };

  const menuVisibility =
    open
      ? 'pointer-events-auto visible translate-y-0 opacity-100 duration-200 ease-out'
      : 'pointer-events-none invisible -translate-y-[5px] opacity-0 duration-100 ease-in';

  return (
    <div
      className="relative inline-flex shrink-0 items-center"
      onMouseEnter={handleWrapperEnter}
      onMouseLeave={scheduleClose}
    >
      <button
        ref={triggerRef}
        type="button"
        className={triggerClass(active)}
        aria-expanded={open}
        aria-haspopup="menu"
        id={`${menuId}-trigger`}
      >
        {label}
      </button>
      <div
        className={
          narrow
            ? `fixed z-[1200] max-h-[min(70vh,calc(100vh-5rem))] overflow-y-auto pt-0 transition-[opacity,transform,visibility] motion-reduce:transition-none ${menuVisibility}`
            : `fixed left-[10vw] right-[10vw] z-[1200] max-h-[min(70vh,calc(100vh-5rem))] overflow-y-auto pt-0 transition-[opacity,transform,visibility] motion-reduce:transition-none ${menuVisibility}`
        }
        role="menu"
        aria-labelledby={`${menuId}-trigger`}
        id={`${menuId}-menu`}
        style={
          narrow
            ? {
                top: topPx != null ? `${topPx}px` : undefined,
                left: 'auto',
                right: rightPx != null ? `${rightPx}px` : undefined,
                width: 'min(20rem, calc(100vw - 1.5rem))',
                maxWidth:
                  anchorRightPx != null
                    ? `min(20rem, calc(100vw - 1.5rem), ${Math.max(0, anchorRightPx - 16)}px)`
                    : undefined,
              }
            : { top: topPx != null ? `${topPx}px` : undefined }
        }
        onMouseEnter={openMenu}
        onMouseLeave={scheduleClose}
      >
        <div
          className={
            narrow
              ? 'w-full overflow-hidden rounded-lg border border-gray-700 bg-gray-900/95 py-2 shadow-lg backdrop-blur-md'
              : 'main-container mx-auto w-full min-w-[12rem] overflow-hidden rounded-lg border border-gray-700 bg-gray-900/95 py-2 shadow-lg backdrop-blur-md'
          }
        >
          {children}
        </div>
      </div>
    </div>
  );
}

/** Desktop primary navigation — hover mega-menus. Hidden below `lg`; mobile uses <MobileNav />. */
function DesktopNavLinks() {
  const pathname = usePathname();
  const { content, contentLoading, contentError } = useHeaderMenuContent();
  const experiences = getVisibleExperiences(content);

  const active = (which: 'destinations' | 'experiences' | 'tours') => {
    if (which === 'destinations') return pathname.startsWith('/destinations/');
    if (which === 'experiences') return pathname.startsWith('/experiences/');
    return pathname === '/tours';
  };

  return (
    <nav
      className="hidden flex-row flex-nowrap items-center justify-end gap-x-4 sm:gap-x-5 lg:flex"
      aria-label="Main"
    >
      <DestinationsNavDropdown active={active('destinations')} menuId="nav-destinations" />

      <NavHoverDropdown label="Experiences" active={active('experiences')} menuId="nav-experiences" narrow>
        {contentLoading ? (
          <p className="px-4 py-3 text-sm text-gray-500">Loading…</p>
        ) : contentError ? (
          <p className="px-4 py-3 text-sm text-gray-500">Could not load experiences.</p>
        ) : experiences.length === 0 ? (
          <p className="px-4 py-3 text-sm text-gray-500">No experiences available.</p>
        ) : (
          <ul className="m-0 max-h-[min(60vh,20rem)] list-none overflow-y-auto p-0" role="none">
            {experiences.map((e) => (
              <li key={e.id} role="none">
                <Link href={paths.experience(e.slug)} className={menuLinkClass} role="menuitem">
                  {e.name}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </NavHoverDropdown>

      <Link
        href={paths.tours()}
        className={`${toursLinkClass} ${active('tours') ? 'text-primary-400' : ''}`}
      >
        Tours
      </Link>
    </nav>
  );
}

function DesktopNavLinksFallback() {
  const pathname = usePathname();
  const activeDest = pathname.startsWith('/destinations/');

  return (
    <nav
      className="hidden flex-row flex-nowrap items-center justify-end gap-x-4 sm:gap-x-5 lg:flex"
      aria-label="Main"
    >
      <DestinationsNavDropdown active={activeDest} menuId="nav-destinations" />

      <NavHoverDropdown label="Experiences" active={false} menuId="nav-experiences" narrow>
        <p className="px-4 py-3 text-sm text-gray-500">Loading…</p>
      </NavHoverDropdown>

      <Link href={paths.tours()} className={toursLinkClass}>
        Tours
      </Link>
    </nav>
  );
}

/** Desktop-only primary navigation. Below `lg` the header renders <MobileNav /> instead. */
export function HeaderNavLinks() {
  return (
    <Suspense fallback={<DesktopNavLinksFallback />}>
      <DesktopNavLinks />
    </Suspense>
  );
}
